

import { useParams, useNavigate } from "react-router-dom"
import { useShow } from "@/contexts/ShowContext"
import { useUser } from "@/contexts/UserContext"
import { useState, useEffect, useRef } from "react"
import Button from "@/components/ui/Button"
import { useModal } from "@/contexts/ModalContext"
import { FiPlus, FiX, FiDownload, FiImage, FiVideo, FiTrash2, FiCopy, FiChevronDown, FiCheck, FiAlertCircle, FiInfo, FiClock, FiMoreVertical } from "react-icons/fi"
import { TbCancel } from "react-icons/tb"
import { FaAngleLeft, FaAngleRight, FaAngleDoubleLeft, FaHeart, FaRegHeart, FaCog } from "react-icons/fa"
import { IoIosRefresh } from "react-icons/io";

import Loader from "@/components/loader"
import useKagamiBridge from "@/hooks/useKagamiBridge"
import { useWebSocket } from "@/hooks/useWebSocket"
import { LuAudioLines, LuText, LuVideo, LuImage } from "react-icons/lu"
import AiModelEditor, { ModelSelectorDialog, generationMediaDragType } from "@/components/AiModelEditor"
import { Input } from "@/components/ui/shadcn/input"
import { Checkbox } from "@/components/ui/shadcn/checkbox"
import AssetEditorModal from "@/components/modals/AssetEditorModal"
import VideoPlayer from "@/components/VideoPlayer"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/shadcn/tabs"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/shadcn/dropdown-menu"
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/shadcn/resizable"
import { Button as ShadButton } from "@/components/ui/shadcn/button"
import TaskModal from "@/components/modals/TaskModal"
import ConfirmModal from "@/components/modals/ConfirmModal"
import { toast } from "sonner"
import dayjs from 'dayjs'
import { GENERATION_DRAG_TYPE } from "@/components/storyboard/StoryboardGenerations"
import { DragPreviewProvider, useDragPreview } from "@/components/ui/DragPreview"
import { apiFetch } from '@/lib/api'

function TaskItem({ task, onClick, onRecallSettings, onTaskUpdated }) {
    const { cancelTask, deleteTask } = useKagamiBridge()
    const { openModal } = useModal()
    const [loading, setLoading] = useState(false)
    const [liveState, setLiveState] = useState(task.state)
    const [liveStatus, setLiveStatus] = useState(task.status)

    const isActive = ['pending', 'in_progress', 'scheduled'].includes(liveState)

    useWebSocket(isActive ? ['task_updated','task_completed','task_failed','task_cancelled'] : [], {
        onMessage: (message) => {
            if (message.data?.taskId !== task._id) return
            const update = message.data
            if (update.state) setLiveState(update.state)
            if (update.status) setLiveStatus(update.status)
            if (['completed', 'failed', 'cancelled'].includes(update.state)) {
                onTaskUpdated?.()
            }
        },
    })

    const handleCancel = async () => {
        const confirmed = await openModal(<ConfirmModal title="Cancel Task" message="Are you sure you want to cancel this task?"/>)
        if(!confirmed) return

        try {            
            setLoading(true)
            await cancelTask(task._id)
            toast.success('Task cancelled', {
                description: 'Task has been cancelled',
                duration: 5000,
                position: 'top-center',
            })
            onTaskUpdated?.()
        } catch (error) {
            console.error('Error cancelling task:', error)
            toast.error('Failed to cancel task', {
                description: error.message,
                duration: 5000,
                position: 'top-center',
            })
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        const confirmed = await openModal(<ConfirmModal title="Delete Task" message="Are you sure you want to delete this task?"  />)
        if(!confirmed) return
        
        try {
            setLoading(true)
            await deleteTask(task._id)
            toast.success('Task deleted', {
                description: 'Task has been deleted',
                duration: 5000,
                position: 'top-center',
            })
            onTaskUpdated?.()
        } catch (error) {
            console.error('Error deleting task:', error)
            toast.error('Failed to delete task', {
                description: error.message,
                duration: 5000,
                position: 'top-center',
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className={`flex items-center gap-2 select-none cursor-pointer hover:bg-card px-1 py-2 border-b border-b-secondary ${loading ? 'opacity-30 animate-pulse pointer-events-none' : ''}`} onClick={onClick}>
            <div className='flex items-center justify-center w-4 h-4'>
                {liveState === 'cancelled' && <TbCancel className='w-4 h-4 text-gray-600' />}
                {liveState === 'scheduled' && <FiClock className='w-3 h-3 text-gray-300' />}
                {liveState === 'completed' && <FiCheck className='w-3 h-3 text-green-500' />}
                {liveState === 'failed' && <FiAlertCircle className='w-4 h-4 text-red-500' />}
                {(liveState === 'in_progress' || liveState === 'pending') && <FaCog className='w-3 h-3 text-secondary-foreground/80 animate-spin' />}
            </div>
            <span className='text-secondary-foreground text-xs flex-1 flex items-center gap-2'>
                <span className='text-secondary-foreground text-[10px] bg-secondary/60 rounded-sm px-1 py-0.5'>{task.model?.name}</span>
                <span className='text-secondary-foreground text-[10px]'>{liveStatus}</span>
            </span>
            
            <span className='text-secondary-foreground/80 text-[10px]'>{dayjs(task.createdAt).format('DD/MM/YYYY HH:mm')}</span>
            
            <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <div className='p-1 rounded-sm hover:bg-secondary/60 text-secondary-foreground/60 hover:text-secondary-foreground'>
                        <FiMoreVertical className='w-4 h-4' />
                    </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem onClick={onRecallSettings}>
                        <IoIosRefresh className='w-4 h-4' />
                        <span>Recall settings</span>
                    </DropdownMenuItem>
                    {liveState === 'scheduled' && (
                        <DropdownMenuItem onClick={handleCancel}>
                            <TbCancel className='w-4 h-4' />
                            <span>Cancel</span>
                        </DropdownMenuItem>
                    )}
                    {['failed', 'scheduled', 'cancelled'].includes(liveState) && <DropdownMenuItem onClick={handleDelete}>
                        <FiTrash2 className='w-4 h-4' />
                        <span>Clear</span>
                    </DropdownMenuItem>}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
}

export default function StudioPageWrapper(props) {
    return <DragPreviewProvider><StudioPage {...props} /></DragPreviewProvider>
}

function StudioPage({ children }) {
    const [assets, setAssets] = useState([])
    const {show} = useShow()
    const {user} = useUser()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [prompt, setPrompt] = useState('')
    const [updating, setUpdating] = useState(false)
    const [preview, setPreview] = useState({})
    const [isDragOver, setIsDragOver] = useState(false)
    const {openModal} = useModal()
    const generationScrollRef = useRef(null)
    const [selectedModel, setSelectedModel] = useState(null)
    const [characters, setCharacters] = useState([])
    const [selectedCharacter, setSelectedCharacter] = useState(null)
    const [modelsDialogOpen, setModelsDialogOpen] = useState(false)
    const [generations, setGenerations] = useState([])
    const [genTotalPages, setGenTotalPages] = useState(0)
    const [genTotal, setGenTotal] = useState(0)
    const [genCurrentPage, setGenCurrentPage] = useState(1)
    const [genPageSize, setGenPageSize] = useState(50)
    const [genLiked, setGenLiked] = useState(false)
    const [genFailed, setGenFailed] = useState(true)
    const [genSearch, setGenSearch] = useState('')
    const [genLoading, setGenLoading] = useState(false)
    const [genTypes, setGenTypes] = useState({image: true, video: true, audio: true})
    const [models, setModels] = useState([])
    const [modelData, setModelData] = useState({})
    const [hasCategoryFilter, setHasCategoryFilter] = useState(false)
    const [tasks, setTasks] = useState([])
    const [tasksLoading, setTasksLoading] = useState(false)
    const [activeTasks, setActiveTasks] = useState(0)
    const [completedTasks, setCompletedTasks] = useState(0)
    const [failedTasks, setFailedTasks] = useState(0)
    const {runTask} = useKagamiBridge()
    const { showDragPreview } = useDragPreview()
    const [script, setScript] = useState(null)

    const [activeTab, setActiveTab] = useState('generations')
    const activeTabRef = useRef(activeTab)
    activeTabRef.current = activeTab

    useWebSocket(['task_completed', 'task_failed', 'task_cancelled'], {
        onMessage: () => {
            fetchGenerations()
            fetchTasks()
        },
    })

    const fetchModels = async()=>{
        // Read URL query params for filtering
        const searchParams = new URLSearchParams(window.location.search)
        const apiParams = new URLSearchParams()
        
        const group = searchParams.get('group')
        const category = searchParams.get('category')
        
        if (group) apiParams.set('group', group)
        if (category) apiParams.set('category', category)
        
        // Track if category filter is applied via URL
        setHasCategoryFilter(!!category)
        
        const queryString = apiParams.toString()
        const response = await apiFetch(`/api/models${queryString ? `?${queryString}` : ''}`)
        if(!response.ok){
            throw new Error('Failed to fetch models')
        }
        const data = await response.json()
        setModels(data)
        
        // Check localStorage for previously selected model
        const savedModelId = localStorage.getItem('kagami-studio-selected-model')
        const savedModel = savedModelId ? data.find(m => m._id === savedModelId) : null
        const initialModel = savedModel || data[0]
        
        setSelectedModel(initialModel)
        const md = {}
        if(initialModel.inputs.length){
            initialModel.inputs.forEach(input => {
                md[input.prop] = input.default
            })
        }
        setModelData(md)
    }

    const fetchAssets = async()=>{
        const searchParams = new URLSearchParams(window.location.search);
        const queryParams = {};
        
        for (const [key, value] of searchParams.entries()) {
            queryParams[key] = value;
        }

        if(queryParams.assets) {

            const assetsIds = queryParams.assets.indexOf(',') !== -1 ? queryParams.assets.split(',') : [queryParams.assets]

            // if (typeof window !== "undefined" && window.history && window.location) {
            //     const url = new URL(window.location.href);
            //     url.searchParams.delete('assets');
            //     window.history.replaceState({}, document.title, url.pathname + url.search);
            // }

            for(const assetId of assetsIds){
                try{
                    const response = await apiFetch(`/api/shows/${show._id}/assets/${assetId}`)
                    if (!response.ok) throw new Error('Failed to fetch asset')
                    const asset = await response.json()

                    setAssets(prev => Array.from(new Set([...prev, asset.url])))                
                    setPreview({url: asset.url, type: asset.mediaType})
                }catch(error){
                    console.error('Error fetching asset:', error)
                }
            }
        }

        setLoading(false)
    }

    const fetchGenerations = async()=>{
        try{
            //setGenLoading(true)
            const params = new URLSearchParams()
            params.set('page', genCurrentPage)
            params.set('size', genPageSize)
            params.set('liked', genLiked.toString())
            params.set('failed', genFailed.toString())
            params.set('search', genSearch)
            params.set('types', Object.keys(genTypes).filter(key => genTypes[key]).join(','))

            const response = await apiFetch(`/api/shows/${show._id}/generations?${params.toString()}`)
            if(!response.ok){
                throw new Error('Failed to fetch generations')
            }
            const payload = await response.json()
            setGenerations(payload.data)
            setGenTotalPages(payload.totalPages)
            setGenTotal(payload.totalGenerations)
            if(generationScrollRef.current){
                generationScrollRef.current.scrollTop = 0
            }
        }catch(error){
            console.error('Error fetching generations:', error)
        }
    }

    const fetchTasks = async()=>{
        try{
            // setTasksLoading(true)
            const response = await apiFetch(`/api/tasks?show=${show._id}`)
            if(!response.ok){
                throw new Error('Failed to fetch tasks')
            }
            const data = await response.json()
            setTasks(data.tasks)

            setActiveTasks(data.active)
            setCompletedTasks(data.completed)
            setFailedTasks(data.failed)
        }
        catch(error){
            console.error('Error fetching tasks:', error)
        }
        finally{
            // setTasksLoading(false)
        }
    }

    const fetchCharacters = async()=>{
        try{
        const response = await apiFetch(`/api/shows/${show._id}/characters`)
        if(!response.ok){
            throw new Error('Failed to fetch characters')
        }
            const data = await response.json()
            const ch = data.filter(c=>c.voice_ids.length).map(c=>({
                name: c.name,
                value: c.voice_ids[0].voice_id
            }))

            if(ch.length){
                setSelectedCharacter(ch[0].value)
            }

            setCharacters(ch)
        }catch(error){
            console.error('Error fetching characters:', error)
        }
    }

    const updateGeneration = async(generationId, data)=>{
        try{
            const response = await apiFetch(`/api/shows/${show._id}/generations/${generationId}`, {
                method: 'PUT',
                body: JSON.stringify(data)
            })

            if(!response.ok){
                throw new Error('Failed to update generation')
            }

            const newGeneration = await response.json()
            setGenerations(prev => prev.map(g => g._id === generationId ? newGeneration : g))
        }
        catch(error){
            console.error('Error updating generation:', error)
        }
        finally{
            
        }
    }

    useEffect(()=>{      
        fetchModels()
        fetchAssets()
        fetchTasks()
        fetchCharacters()
    },[])

    useEffect(()=>{
        const getData = async()=>{
            try{
                setGenLoading(true)
                await fetchGenerations()
            }catch(error){
                console.error('Error fetching generations:', error)
            }finally{
                setGenLoading(false)
            }
        }
        getData()
    },[genCurrentPage, genLiked, genFailed, genTypes])

    useEffect(()=>{
        const getData = async()=>{
            if(genCurrentPage>1){
                setGenCurrentPage(1)
            }else{
                setGenLoading(true)
                await fetchGenerations()
                setGenLoading(false)
            }
        }
        const debounced = setTimeout(() => {
            getData()
        }, 500)
        return () => clearTimeout(debounced)
    },[genSearch])

    const handleChatSubmit = async ({ totalGenerations = 1, run_at = null }) => {
        const groupId = crypto.randomUUID()
        for(let i=0; i< totalGenerations; i++){
            await runTask( {
                modelId: selectedModel._id, 
                payload: modelData, 
                showId: show._id, 
                group: groupId, 
                ...(run_at && {run_at: run_at})
            })
        }
        await fetchTasks()
    }

    const handleSaveGeneration = async (generation) => {
        if(!generation || !generation.url) return

        const newAsset = await openModal(<AssetEditorModal asset={{
            url: generation.url,
            thumbnail_url: generation.task?.request_body?.payload?.asset || generation.task?.request_body?.payload?.assets?.[0],
            name: 'New Asset',
            description: generation.prompt,
            mediaType: generation.type,
            source: 'studio'
        }} />, 'side')

        if(!newAsset) return

        try{
            const response = await apiFetch(`/api/shows/${show._id}/assets`, {
                method: 'POST',
                body: JSON.stringify({
                    _id: generation._id,
                    url: generation.url,
                    thumbnail_url: generation.type === 'video' ? (generation.task?.request_body?.payload?.asset || generation.task?.request_body?.payload?.assets?.[0]) : generation.url,
                    name: newAsset.name,
                    description: newAsset.description,
                    mediaType: generation.type,
                    source: 'studio'
                })
            })

            if(!response.ok){

                throw new Error('Failed to save generation')
            }
    
            toast("Done!", {
                description: `Asset "${newAsset.name}" has been saved`,
                duration: 5000,
                position: 'top-center',
                action: {
                  label: "View",
                  onClick: () => {
                    navigate('/show/assets');
                  },
                },
            })
        }catch(error){
            console.error('Error saving generation:', error)
        }
        finally{
            
        }

    }

    const handleDownloadAsset = async (asset) => {
        if(!asset || !asset.url) return
        const blob = await fetch(asset.url).then(res => res.blob())
        const mimeType = blob.type
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `asset_${Date.now()}.${mimeType.split('/')[1]}`
        link.click()
        URL.revokeObjectURL(url)
    }

    useEffect(()=>{
        if (selectedModel) {
            // Save selected model to localStorage
            localStorage.setItem('kagami-studio-selected-model', selectedModel._id)

            const md = {};
            if(selectedModel.inputs?.length){
                selectedModel.inputs.forEach(input => {
                    const raw = modelData[input.prop];
                    const existingValue = (raw === '' || raw == null) ? undefined : raw;
                    let value;
                    if (input.type === 'options' && input.enum && Array.isArray(input.enum)) {
                        value = input.enum.some(e => e.key === existingValue) ? existingValue : input.default;
                    } else {
                        value = existingValue ?? input.default;
                    }
                    if (value !== undefined) md[input.prop] = value;
                })
            }

            setModelData(md);
        }
    },[selectedModel])

    useEffect(()=>{
        if (typeof window !== 'undefined' && window.event && (window.event.metaKey || window.event.ctrlKey)) {
            //TODO Restore settings from generation payload
        }
        
    },[preview])

    const recallTaskSettings = (task) => {
        // 1. Find and select the model based on task's modelId
        const model = models.find(m => m._id === task.model?._id || m._id === task.model)
        if (model) {
            setSelectedModel(model)
        }
        
        // 2. Apply the payload settings to modelData
        if (task.request_body?.payload) {
            setModelData(task.request_body.payload)
        }
    }

    return (
        <div className='flex flex-col w-full h-full relative'>
            <div className='flex items-center justify-between px-4 h-20'>
                <div className='flex items-center gap-2'>
                    <div className='text-xl border-l-4 border-primary pl-4 pr-2 py-1 bg-secondary rounded-r-md flex gap-2 items-center cursor-pointer' onClick={()=>setModelsDialogOpen(true)}>
                        {selectedModel?.group} - {selectedModel?.name} 
                        <FiChevronDown className='w-4 h-4' />
                    </div>
                                       
                    <ModelSelectorDialog
                        open={modelsDialogOpen}
                        onOpenChange={setModelsDialogOpen}
                        models={models}
                        selectedModel={selectedModel}
                        onSelectModel={setSelectedModel}
                        hasCategoryFilter={hasCategoryFilter}
                    />
                </div>
                <div className='flex items-center gap-2'>
                    
                </div>
            </div>
            {/* <div className='flex flex-1 px-4 gap-2 overflow-hidden pb-2'> */}
            <ResizablePanelGroup direction="horizontal" className='flex flex-1 px-4 overflow-hidden pb-2'>
                <ResizablePanel className="flex-1/4 flex flex-col overflow-y-hidden " defaultSize={300} minSize={200}>
                    <AiModelEditor
                        selectedModel={selectedModel}
                        modelData={modelData}
                        onModelDataChange={setModelData}
                        preview={preview}
                        onPreviewChange={setPreview}
                        onSubmit={handleChatSubmit}
                    />
                </ResizablePanel>
                <ResizableHandle withHandle className='w-2 h-full opacity-30 hover:opacity-60 border-0 focus-visible:outline-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 '/>
                <ResizablePanel className='flex-3/4 flex flex-col gap-1'>
                    <div className='border rounded-md flex-1 overflow-hidden flex flex-col relative'>
                        <div className='flex p-1 justify-between border-b'>
                            <div className='flex items-center gap-1'>
                                
                            </div>
                            <div className='flex items-center gap-1'>
                                <Button type='secondary' size='sm' icon={<FiPlus className='w-4 h-4' />} onClick={()=>handleSaveGeneration(preview)} disabled={!preview.url}>
                                    <span>Save as Asset</span>
                                </Button>
                                <Button type='secondary' size='sm' icon={<FiDownload className='w-4 h-4' />} onClick={()=>handleDownloadAsset(preview)} disabled={!preview.url}>
                                    <span>Download</span>
                                </Button>
                            </div>
                        </div>
                        <div className='flex-1 flex justify-center items-center overflow-hidden select-none bg-black' >
                            {preview && <div
                                className='relative w-full h-full relative group flex items-center cursor-grab active:cursor-grabbing'
                                draggable={!!preview.url}
                                onDragStart={(e) => {
                                    if (!preview.url) { e.preventDefault(); return }
                                    const thumbUrl = preview.type === 'image' ? preview.url : (preview.task?.request_body?.payload?.asset || preview.task?.request_body?.payload?.assets?.[0] || null)
                                    showDragPreview(e, { thumbnailUrl: thumbUrl, name: preview.prompt || 'Preview', mediaType: preview.type })
                                    e.dataTransfer.setData(GENERATION_DRAG_TYPE, JSON.stringify({ url: preview.url, type: preview.type }))
                                    e.dataTransfer.setData(generationMediaDragType(preview.type), '')
                                    e.dataTransfer.effectAllowed = 'copy'
                                }}
                            >
                                {preview.type === 'image' && <img src={preview.url} alt='preview' decoding="async" className='w-full h-full object-contain pointer-events-none' draggable={false} />}
                                {preview.type === 'video' && <VideoPlayer src={preview.url} className='w-full h-full' />}
                                {preview.type === 'audio' && <audio src={preview.url} controls className='w-full h-full object-contain' />}
                            </div>}
                        </div>
                        
                        {preview?.task && (
                            <div className='flex flex-col gap-3 text-xs p-2 border-t max-h-1/3 overflow-y-auto'>
                                <div className='flex flex-col gap-1'>
                                    <div className='flex items-center gap-2'>
                                        <span className='text-secondary-foreground/60 font-medium uppercase text-[10px]'>Model</span>
                                    </div>
                                    <div className='flex items-center gap-2'>
                                        <span className='bg-secondary px-2 py-0.5 rounded-sm text-secondary-foreground'>
                                            {preview.model || 'Unknown Model'}
                                        </span>
                                    </div>
                                </div>

                                {preview.task.request_body?.payload && (
                                    <div className='flex flex-col gap-1'>
                                        <span className='text-secondary-foreground/60 font-medium uppercase text-[10px]'>Settings</span>
                                        {preview.task.request_body.payload.prompt && (
                                                <div className='flex flex-col gap-0.5 bg-secondary/50 rounded-md p-2 flex-1 min-w-48 w-full relative group/prompt'>
                                                    <span className='text-secondary-foreground/60 text-[10px]'>Prompt</span>
                                                    <div className='flex items-center justify-between'>
                                                        <span className='text-secondary-foreground text-xs font-mono'>{preview.task.request_body.payload.prompt}</span>
                                                        {selectedModel?.inputs?.find(input => input.prop === 'prompt') && (
                                                            <Button type='secondary' size='sm' icon={<FiCopy className='w-3 h-3' />} onClick={() => setModelData(prev => ({...prev, prompt: preview.task.request_body.payload.prompt}))}>
                                                                <span>Reuse</span>
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        <div className='flex flex-wrap gap-2'>
                                            
                                            {Object.entries(preview.task.request_body.payload)
                                                .filter(([key, value]) => 
                                                    !['prompt', 'asset', 'assets', 'video', 'audio'].includes(key) && 
                                                    value !== null && 
                                                    value !== undefined && 
                                                    value !== ''
                                                )
                                                .map(([key, value]) => (
                                                    <div key={key} className='flex items-center gap-2 bg-secondary/50 rounded-md px-2 py-1'>
                                                        <span className='text-secondary-foreground/60 text-[10px] capitalize'>{key.replace(/_/g, ' ')}</span>
                                                        <span className='text-secondary-foreground text-xs'>
                                                            {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                                                        </span>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    </div>
                                )}

                                <div className='flex flex-col gap-1'>
                                    <span className='text-secondary-foreground/60 font-medium uppercase text-[10px]'>Media</span>
                                    <div className='flex flex-wrap gap-2'>
                                        <div className='flex items-center gap-2 bg-secondary/50 rounded-md px-2 py-1'>
                                            <span className='text-secondary-foreground/60 text-[10px]'>Type</span>
                                            <span className='text-secondary-foreground text-xs capitalize'>{preview.type}</span>
                                        </div>
                                        {preview.task.request_body?.payload?.width && (
                                            <div className='flex items-center gap-2 bg-secondary/50 rounded-md px-2 py-1'>
                                                <span className='text-secondary-foreground/60 text-[10px]'>Width</span>
                                                <span className='text-secondary-foreground text-xs'>{preview.task.request_body.payload.width}px</span>
                                            </div>
                                        )}
                                        {preview.task.request_body?.payload?.height && (
                                            <div className='flex items-center gap-2 bg-secondary/50 rounded-md px-2 py-1'>
                                                <span className='text-secondary-foreground/60 text-[10px]'>Height</span>
                                                <span className='text-secondary-foreground text-xs'>{preview.task.request_body.payload.height}px</span>
                                            </div>
                                        )}
                                        {preview.task.request_body?.payload?.resolution && (
                                            <div className='flex items-center gap-2 bg-secondary/50 rounded-md px-2 py-1'>
                                                <span className='text-secondary-foreground/60 text-[10px]'>Resolution</span>
                                                <span className='text-secondary-foreground text-xs'>{preview.task.request_body.payload.resolution}</span>
                                            </div>
                                        )}
                                        {preview.task.request_body?.payload?.aspect_ratio && (
                                            <div className='flex items-center gap-2 bg-secondary/50 rounded-md px-2 py-1'>
                                                <span className='text-secondary-foreground/60 text-[10px]'>Aspect Ratio</span>
                                                <span className='text-secondary-foreground text-xs'>{preview.task.request_body.payload.aspect_ratio}</span>
                                            </div>
                                        )}
                                        {preview.task.request_body?.payload?.duration && (
                                            <div className='flex items-center gap-2 bg-secondary/50 rounded-md px-2 py-1'>
                                                <span className='text-secondary-foreground/60 text-[10px]'>Duration</span>
                                                <span className='text-secondary-foreground text-xs'>{preview.task.request_body.payload.duration}s</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        
                    </div>
                </ResizablePanel>
                <ResizableHandle withHandle className='w-2 h-full opacity-30 hover:opacity-60 border-0 focus-visible:outline-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 '/>
                <ResizablePanel className='flex min-w-52' defaultSize={'25%'} minSize={300} >
                    <Tabs defaultValue="generations" className="w-full h-full" onValueChange={(value) => { setActiveTab(value); if (value === 'tasks' && activeTasks > 0) fetchTasks() }}>
                        <TabsList className="w-full">
                            <TabsTrigger value="generations" className="text-xs " >Generations</TabsTrigger>
                            <TabsTrigger value="tasks" className="text-xs flex items-center gap-2">
                                Tasks
                                {activeTasks > 0 && <span className='text-[10px] anim-pulse min-w-4 rounded-sm bg-primary px-[2px] py-[1px] flex items-center justify-center text-primary-foreground'>{activeTasks}</span>}
                            </TabsTrigger>
                        </TabsList>
                        <TabsContent value="generations" className="flex flex-col gap-2 overflow-hidden relative">
                            <div className='flex flex-col gap-2'>
                                <div className='flex w-full relative'>
                                    <Input type='text' placeholder='Search for Generation...' className='w-full' value={genSearch} onChange={(e) => setGenSearch(e.target.value)} />
                                    {genSearch && <FiX className='w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-secondary-foreground/50 hover:text-secondary-foreground cursor-pointer' onClick={() => setGenSearch('')} />}
                                </div>
                                <div className='flex items-center gap-x-6 gap-y-2 px-1 flex-wrap'>
                                    
                                    {Object.keys(genTypes).map((key) => (<div className='flex items-center gap-2' key={key}>
                                        <Checkbox id={`filter-${key}`} name={`filter-${key}`} checked={genTypes[key]} onCheckedChange={(checked) => setGenTypes(prev => ({...prev, [key]: checked}))} />
                                        <label htmlFor={`filter-${key}`} className='text-[10px] text-secondary-foreground capitalize select-none capitalize'>{key}</label>
                                    </div>))}

                                    {/* <div className='flex items-center gap-2'>
                                        <Checkbox id='filterFailed' name='filterFailed' checked={genFailed} onCheckedChange={(checked) => setGenFailed(checked)} />
                                        <label htmlFor='filterFailed' className='text-[10px] text-secondary-foreground capitalize select-none'>Failed</label>
                                    </div> */}

                                    <div className='flex items-center gap-2'>
                                        <Checkbox id='filterLiked' name='filterLiked' checked={genLiked} onCheckedChange={(checked) => setGenLiked(checked)} />
                                        <label htmlFor='filterLiked' className='text-[10px] text-secondary-foreground capitalize select-none'>Liked</label>
                                    </div> 
                                </div>
                            </div>
                            <div ref={generationScrollRef} className='flex flex-1 flex-col gap-3 overflow-y-auto rounded-md' >
                                {(() => {
                                    // Group generations by their group property
                                    const groupedGenerations = generations.reduce((acc, generation) => {
                                        const groupKey = generation.group || generation._id // Use _id as key for ungrouped items
                                        if (!acc[groupKey]) {
                                            acc[groupKey] = []
                                        }
                                        acc[groupKey].push(generation)
                                        return acc
                                    }, {})

                                    return Object.entries(groupedGenerations).map(([groupKey, groupGens]) => (
                                        <div key={groupKey} className="flex-none flex flex-col relative overflow-hidden group/gen-group">  
                                            <div className={`bg-card gap-1 ${groupGens.length > 1 ? 'grid grid-cols-2' : 'flex flex-col'}`}>
                                                {groupGens.map((generation) => (
                                                    <div key={generation._id} className="relative flex-none h-auto select-none group/generation">
                                                        {generation.url && generation.task?.state==='completed' &&   
                                                            <div
                                                                className={`w-full h-auto relative cursor-grab active:cursor-grabbing overflow-hidden min-h-9/16 border rounded-md  ${preview === generation ? 'border-primary' : ''}`}
                                                                onClick={()=>setPreview(generation)}
                                                                draggable
                                                                onDragStart={(e) => {
                                                                    const thumbUrl = generation.type === 'image' ? generation.url : (generation.task?.request_body?.payload?.asset || generation.task?.request_body?.payload?.assets?.[0] || null)
                                                                    showDragPreview(e, { thumbnailUrl: thumbUrl, name: generation.prompt || 'Generation', mediaType: generation.type })
                                                                    e.dataTransfer.setData(GENERATION_DRAG_TYPE, JSON.stringify({ url: generation.url, type: generation.type }))
                                                                    e.dataTransfer.setData(generationMediaDragType(generation.type), '')
                                                                    e.dataTransfer.effectAllowed = 'copy'
                                                                    e.stopPropagation()
                                                                }}
                                                            >
                                                                {generation.thumbnail_url ? 
                                                                    <img src={generation.thumbnail_url} alt={generation.prompt || generation._id} loading="lazy" decoding="async" className='object-contain w-full h-auto pointer-events-none' draggable={false} />
                                                                    :
                                                                    <div className='w-full aspect-video flex items-center justify-center'>
                                                                        {generation.type==='audio' && <LuAudioLines className='w-1/3 h-1/3 text-secondary-foreground/20' />}
                                                                        {generation.type==='video' && 
                                                                            // <LuVideo className='w-1/3 h-1/3 text-secondary-foreground/20' />
                                                                            <video src={generation.url} muted className='object-contain w-full h-auto pointer-events-none' draggable={false} /> // temp falback
                                                                        }
                                                                        {generation.type==='image' && <LuImage className='w-1/3 h-1/3 text-secondary-foreground/20' />}
                                                                    </div>
                                                                }
                                                                {/* {generation.type==='video11' && <video src={generation.url} muted className='object-contain w-full h-auto pointer-events-none' draggable={false} />} */}
                                                                {/* {(generation.type==='video' && !generation.thumbnail_url) && (
                                                                    (generation.task?.request_body?.payload?.asset || generation.task?.request_body?.payload?.assets?.[0])
                                                                        ? <img src={generation.task?.request_body?.payload?.asset || generation.task?.request_body?.payload?.assets[0]} alt={generation.prompt || generation._id} loading="lazy" decoding="async" className='object-contain w-full h-auto pointer-events-none' draggable={false} />
                                                                        : <video src={generation.url} muted className='object-contain w-full h-auto pointer-events-none' draggable={false} />
                                                                )}
                                                                {generation.type==='audio' && <div src={generation.url} muted className='w-full aspect-video flex items-center justify-center'>
                                                                    <LuAudioLines className='w-8 h-8 text-secondary-foreground/80' />
                                                                </div>} */}
                                                                <div className='absolute top-0 left-0 w-full h-full bg-background/50 hidden items-center justify-center pointer-events-none group-hover/generation:flex'>
                                                                    <div className='absolute bottom-0 right-0 p-1 pointer-events-auto'>
                                                                        <div className='flex items-center bg-card rounded-lg border'>
                                                                            <div className="p-2 flex items-center justify-center text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer" onClick={(e) => {e.stopPropagation(); handleSaveGeneration(generation)}} >
                                                                                <FiPlus className="w-3 h-3" />
                                                                                {/* <span className='text-[10px]'>Save as Asset</span> */}
                                                                            </div>
                                                                            <div className="p-2 flex items-center justify-center text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer" onClick={(e) => {e.stopPropagation(); handleDownloadAsset(generation)}}>
                                                                                <FiDownload className="w-3 h-3"  />
                                                                                {/* <span className='text-[10px]'>Download</span> */}
                                                                            </div>
                                                                            <div className="p-2 flex items-center justify-center text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer" onClick={(e) => {e.stopPropagation(); recallTaskSettings(generation.task)}}>
                                                                                <IoIosRefresh className="w-3 h-3"  />
                                                                                {/* <span className='text-[10px]'>Use Settings</span> */}
                                                                            </div>
                                                                            <div className="p-2 flex items-center justify-center text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer" onClick={(e) => {e.stopPropagation(); openModal(<TaskModal task={generation.task} />, 'side')}}>
                                                                                <FiInfo className="w-3 h-3"  />
                                                                                {/* <span className='text-[10px]'>Task</span> */}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className='absolute top-0 right-0 p-1 pointer-events-auto'>
                                                                    <div className={`px-2 py-1 rounded-lg flex items-center justify-center text-red-500 cursor-pointer group/like-button group-hover:bg-card/80 group-hover/generation:border`} onClick={(e) => {e.stopPropagation(); updateGeneration(generation._id, {liked: !generation.liked})}}>
                                                                        <FaHeart className={`w-4 h-4 group-hover/generation:w-3 group-hover/generation:h-3 ${generation.liked ? 'block group-hover/like-button:hidden' : 'hidden group-hover/like-button:block'}`} />
                                                                        <FaRegHeart className={`w-3 h-3 opacity-0 group-hover/generation:opacity-100 ${generation.liked ? 'hidden group-hover/like-button:block' : 'block group-hover/like-button:hidden'}`} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        }
                                                        {generation.task?.state==='failed' && <div className='bg-card rounded-lg flex flex-col gap-1 px-1 pt-6 pb-1 overflow-hidden'>
                                                            <span className='text-destructive-foreground text-[10px]'>Failed to generate</span>
                                                            <span className='text-destructive-foreground text-[10px]'>{generation.task?.status}</span>
                                                            <div className='absolute bottom-0 right-0 p-1 pointer-events-auto hidden group-hover:flex'>
                                                                <div className='flexitems-center bg-card rounded-lg border'>
                                                                    <div className="p-2 flex items-center justify-center text-secondary-foreground/80 hover:text-secondary-foreground cursor-pointer">
                                                                        <FiCopy className="w-3 h-3" onClick={(e) => {e.stopPropagation(); navigator.clipboard.writeText(generation.task?.status)}} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>}
                                                    </div>
                                                ))}
                                            </div>

                                            {groupGens[0].task?.request_body?.payload?.prompt && <div className='flex group-hover/gen-group:hidden absolute bottom-1 left-1 right-1 pointer-events-none'>
                                                <div className='text-xs text-secondary-foreground/80 py-1 px-2 bg-background/80 rounded-md flex gap-2'>
                                                    {groupGens[0].type==='image' && <FiImage className='w-4 h-4 text-secondary-foreground/60 flex-shrink-0' />}
                                                    {groupGens[0].type==='video' && <FiVideo className='w-4 h-4 text-secondary-foreground/60 flex-shrink-0' />}
                                                    {groupGens[0].type==='audio' && <LuAudioLines className='w-4 h-4 text-secondary-foreground/60 flex-shrink-0' />}
                                                    <span className='text-secondary-foreground/80 text-xs'>{groupGens[0].task?.request_body?.payload?.prompt.split(' ').length > 20 ? groupGens[0].task?.request_body?.payload?.prompt.split(' ').slice(0, 20).join(' ') + '...' : groupGens[0].task?.request_body?.payload?.prompt}</span>
                                                </div>
                                            </div>}

                                            {/* <div className='flex group-hover/gen-group:hidden absolute top-1 left-1 px-2 py-1 gap-2 items-center pointer-events-none bg-background/80 rounded-md'>
                                                {groupGens[0].type==='image' && <FiImage className='w-3 h-3 text-secondary-foreground/80' />}
                                                {groupGens[0].type==='video' && <FiVideo className='w-3 h-3 text-secondary-foreground/80' />}
                                                {groupGens[0].type==='audio' && <LuAudioLines className='w-3 h-3 text-secondary-foreground/80' />}
                                                <span className='text-secondary-foreground text-[10px]'>{groupGens[0].model}</span>
                                            </div> */}

                                        </div>
                                    ))
                                })()}
                            </div>
                            <div className='flex items-center gap-2 p-2 text-xs text-secondary-foreground rounded-md border bg-card'>
                                <Button type='secondary' size='sm' icon={<FaAngleDoubleLeft className='w-4 h-4' />} onClick={()=>setGenCurrentPage(1)} disabled={genCurrentPage === 1}/>
                                <Button type='secondary' size='sm' icon={<FaAngleLeft className='w-4 h-4' />} onClick={()=>setGenCurrentPage(Math.max(1, genCurrentPage - 1))} disabled={genCurrentPage === 1}/>
                                <span>{genCurrentPage}/{genTotalPages}</span>
                                <Button type='secondary' size='sm' icon={<FaAngleRight className='w-4 h-4' />} onClick={()=>setGenCurrentPage(Math.min(genTotalPages, genCurrentPage + 1))} disabled={genCurrentPage===genTotalPages}/>
                                <span>{`${((genCurrentPage - 1) * genPageSize)+1}-${((genCurrentPage - 1) * genPageSize)+genPageSize > genTotal ? genTotal : ((genCurrentPage - 1) * genPageSize)+genPageSize} of ${genTotal}`}</span>
                            </div>
                            {genLoading && <Loader />}
                        </TabsContent>
                        <TabsContent value="tasks" className="flex flex-col gap-2 overflow-hidden relative">
                            <div></div>
                            <div className='flex flex-col overflow-y-auto flex-1 relative'>
                                {tasks.filter(t => t && t.state).map((task) => (
                                    <TaskItem
                                        key={task._id}
                                        task={task}
                                        onClick={() => openModal(<TaskModal task={task} />, 'side')}
                                        onRecallSettings={() => recallTaskSettings(task)}
                                        onTaskUpdated={fetchTasks}
                                    />
                                ))}
                            </div>
                            {tasksLoading && <Loader />}
                        </TabsContent>
                    </Tabs>
                </ResizablePanel>
            </ResizablePanelGroup>
            {/* </div> */}
        </div>
    )
}